import * as React from 'react';
import { Row, Col } from 'react-bootstrap';
import type { ResolveThunks } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { Field, FieldArray } from 'redux-form';

import type { SkillViewModel } from 'ab-viewModels/skill.viewModel';

import type { EquipmentCostRequestModel } from 'ab-requestModels/equipmentCost.requestModel';

import { BlobStorageImageSizeContainer } from '@acceligentllc/shared/enums/blobStorageImageSizeContainer';

import { bemElement } from 'af-utils/bem.util';

import { DEFAULT_EQUIPMENT_IMAGE } from 'ab-common/constants/value';

import { Button, SegmentLabel } from '@acceligentllc/storybook';

import Input from 'af-fields/Input';
import type { OwnProps as MultiTagSelectProps } from 'af-fields/MultiTagSelect';
import MultiTagSelect from 'af-fields/MultiTagSelect';
import Textarea from 'af-fields/Textarea';

import BackButton from 'af-components/BackButton';
import ImageTag from 'af-components/Image';
import ImageUpload from 'af-components/ImageUpload';
import SubmitButton from 'af-components/SubmitButton';

import * as SkillActions from 'af-actions/skill';

import Header from './Header';
import SkillMultiValueLabel from '../Shared/SkillMultiValueLabel';
import SkillOption from '../Shared/SkillOption';

interface OwnProps extends InjectedFormProps<EquipmentCostRequestModel> {
	formName: string;
	onSubmit: (form: EquipmentCostRequestModel) => Promise<void>;
}

interface DispatchProps {
	findAllForCompanyByQuery: typeof SkillActions.findAllForCompanyByQuery;
}

type ConnectOwnProps = OwnProps;

type Props = ConnectOwnProps & ResolveThunks<DispatchProps>;

interface State {
	image: File | string | null;
	imageUrl: string | null;
	deleteImage: boolean;
	hasReceivedInitialItem: boolean;
	skillList: SkillViewModel[];
	showUploadModal: boolean;
}

class EquipmentCostForm extends React.PureComponent<Props, State> {

	state: State = {
		image: null,
		imageUrl: null,
		deleteImage: false,
		hasReceivedInitialItem: false,
		skillList: [],
		showUploadModal: false,
	};

	static getDerivedStateFromProps(props: Props, state: State) {
		const { initialValues } = props;
		const { hasReceivedInitialItem } = state;

		return (!hasReceivedInitialItem && initialValues)
			? {
				hasReceivedInitialItem: true,
				imageUrl: initialValues.image as string,
			}
			: null;
	}

	async componentDidMount() {
		const { findAllForCompanyByQuery } = this.props;
		const skills = await findAllForCompanyByQuery('');
		this.setState(() => ({ skillList: skills }));
	}

	onSubmit = async (form: EquipmentCostRequestModel) => {
		const { onSubmit } = this.props;
		const { deleteImage, image } = this.state;

		form.image = image;
		form.deleteImage = deleteImage;
		await onSubmit(form);
	};

	deleteImage = () => this.setState(() => ({ image: null, imageUrl: null, deleteImage: true }));

	onImageUploadSave = (image: File, imageUrl: string) => {
		this.setState(() => ({
			image,
			imageUrl,
			deleteImage: false,
		}));
	};

	getOptionValue = (option: SkillViewModel) => `${option.id}`;

	getOptionLabel = (option: SkillViewModel) => option.name;

	openUploadModal = () => this.setState(() => ({ showUploadModal: true }));
	closeUploadModal = () => this.setState(() => ({ showUploadModal: false }));

	render() {
		const {
			handleSubmit,
			formName,
			change,
			submitting,
			invalid,
		} = this.props;
		const { image, imageUrl, skillList, showUploadModal } = this.state;

		const imageUploadButtonClassName = bemElement('image-upload', 'button');

		return (
			<form className="equipment-cost-upsert" onSubmit={handleSubmit(this.onSubmit)}>
				<div className="form-box">
					<Header
						changeField={change}
						formName={formName}
					/>
					<Row className="row row--flex">
						<div className="avatar__container">
							<ImageUpload
								closeModal={this.closeUploadModal}
								onSave={this.onImageUploadSave}
								showModal={showUploadModal}
							/>
							<div className="avatar">
								<ImageTag
									fallbackSrc={DEFAULT_EQUIPMENT_IMAGE}
									minSize={BlobStorageImageSizeContainer.SIZE_200X200}
									src={imageUrl}
									tryOriginal={true}
									tryRoot={true}
								/>
							</div>
							<div className={bemElement('image-upload', 'buttons')}>
								<Button
									customStyleClass={imageUploadButtonClassName}
									icon="ActionImport"
									onClick={this.openUploadModal}
									style="secondary"
									tooltip="Upload"
								/>
								<Button
									customStyleClass={imageUploadButtonClassName}
									disabled={!image && !imageUrl}
									icon="Delete"
									onClick={this.deleteImage}
									style="danger"
									tooltip="Delete"
								/>
							</div>
						</div>
						<div>
							<Row className="row--padded-top">
								<Col sm={24}>
									<Field
										component={Input}
										isRequired={true}
										label="Name (Subcategory)"
										name="subcategory"
										placeholder="Enter subcategory"
										type="text"
									/>
								</Col>
							</Row>
						</div>
					</Row>
					<Row>
						<Col className="m-b-m m-t-m" md={24}>
							<SegmentLabel
								label="Costs"
								tooltip="Equipment costs organized by category, including daily, weekly, and monthly rates, plus mob, op hour, and fuel charges."
							/>
						</Col>
					</Row>
					<Row>
						<Col lg={4} md={8} sm={8}>
							<Field
								addonAfter="$"
								component={Input}
								isZeroIfEmpty={true}
								label="Daily cost"
								name="dailyCost"
								placeholder="Enter Daily cost"
								type="number"
								withThousandsSeparator={true}
							/>
						</Col>
						<Col lg={4} md={8} sm={8}>
							<Field
								addonAfter="$"
								component={Input}
								isZeroIfEmpty={true}
								label="Weekly cost"
								name="weeklyCost"
								placeholder="Enter Weekly cost"
								type="number"
								withThousandsSeparator={true}
							/>
						</Col>
						<Col lg={4} md={8} sm={8}>
							<Field
								addonAfter="$"
								component={Input}
								isZeroIfEmpty={true}
								label="Monthly cost"
								name="monthlyCost"
								placeholder="Enter Monthly cost"
								type="number"
								withThousandsSeparator={true}
							/>
						</Col>
						<Col lg={4} md={8} sm={8}>
							<Field
								addonAfter="$"
								component={Input}
								isZeroIfEmpty={true}
								label="Mob charge"
								name="mobCharge"
								placeholder="Enter Mob charge"
								type="number"
								withThousandsSeparator={true}
							/>
						</Col>
						<Col lg={4} md={8} sm={8}>
							<Field
								addonAfter="$"
								component={Input}
								isZeroIfEmpty={true}
								label="Operating charge"
								name="operatingCharge"
								placeholder="Enter Operating charge"
								type="number"
								withThousandsSeparator={true}
							/>
						</Col>
						<Col lg={4} md={8} sm={8}>
							<Field
								addonAfter="$"
								component={Input}
								isZeroIfEmpty={true}
								label="Fuel cost"
								name="fuelCost"
								placeholder="Enter Fuel cost"
								type="number"
								withThousandsSeparator={true}
							/>
						</Col>
					</Row>
					<Row>
						<Col className="m-b-m m-t-m" md={24}>
							<SegmentLabel
								label="Needed Skills and Licenses"
								tooltip="The skills and licenses required to operate this equipment. If a skill is missing from the dropdown, it can be added via Resources → Skills."
							/>
						</Col>
					</Row>
					<Row>
						<Col md={12}>
							<FieldArray<MultiTagSelectProps<SkillViewModel>>
								component={MultiTagSelect}
								getOptionLabel={this.getOptionLabel}
								getOptionValue={this.getOptionValue}
								label="Skills"
								menuPlacement="top"
								MultiValueLabel={SkillMultiValueLabel}
								name="skills"
								Option={SkillOption}
								options={skillList}
								placeholder="Choose skills"
							/>
						</Col>
						<Col md={12}>
							<Field
								component={Textarea}
								controlCursor={true}
								label="Licenses"
								maxCharacters={750}
								name="licenses"
								placeholder="Enter Licenses"
								rows={3}
								showMaxCharactersLabel={true}
								type="text"
							/>
						</Col>
					</Row>
					<Row className="row--submit">
						<BackButton />
						<SubmitButton
							disabled={invalid}
							reduxFormSubmitting={submitting}
							variant="primary"
						/>
					</Row>
				</div>
			</form>
		);
	}
}

function mapDispatchToProps(): DispatchProps {
	return {
		findAllForCompanyByQuery: SkillActions.findAllForCompanyByQuery,
	};
}

const enhance = compose<React.ComponentClass<OwnProps>>(
	connect<null, DispatchProps, ConnectOwnProps>(null, mapDispatchToProps())
);

export default enhance(EquipmentCostForm);
